import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useGameStore } from '../store/gameStore';
import { COUNTING_SYSTEMS } from '../utils/countingSystems';
import { GameMode } from '../types';
import {
  CardsStackIcon,
  TargetIcon,
  LightningIcon,
  ShuffleIcon
} from './Icons';

const SPEED_PRESETS = [
  { label: '0.5x (2000ms)', value: 2000 },
  { label: '1x (1000ms)', value: 1000 },
  { label: '1.5x (667ms)', value: 667 },
  { label: '2x (500ms)', value: 500 },
  { label: '3x (333ms)', value: 333 }
];

const DURATION_PRESETS = [30, 60, 90, 120];

type TabId = 'all' | 'training' | 'table-simulator' | 'speed-drill';
type SectionId =
  | 'shoe'
  | 'tableRules'
  | 'counting'
  | 'bankroll'
  | 'trainingAids'
  | 'tableSim'
  | 'speedDefaults';

const TAB_CONFIG: { id: TabId; label: string; description: string; icon: React.ReactNode }[] = [
  { id: 'all', label: 'All Settings', description: 'Everything in one place', icon: <TargetIcon size={16} /> },
  { id: 'training', label: 'Hand Trainer', description: 'Single-hand practice', icon: <CardsStackIcon size={16} /> },
  { id: 'table-simulator', label: 'Full Table', description: 'Casino-style multi-seat', icon: <ShuffleIcon size={16} /> },
  { id: 'speed-drill', label: 'Speed Drill', description: 'Timed counting sprint', icon: <LightningIcon size={16} /> },
];

const ALL_SECTIONS: SectionId[] = ['speedDefaults', 'shoe', 'tableRules', 'tableSim', 'counting', 'bankroll', 'trainingAids'];

const TAB_SECTIONS: Record<TabId, SectionId[]> = {
  training: ['shoe', 'tableRules', 'counting', 'bankroll', 'trainingAids'],
  'table-simulator': ['tableSim', 'shoe', 'tableRules', 'counting', 'bankroll', 'trainingAids'],
  'speed-drill': ['speedDefaults'],
  all: ALL_SECTIONS,
};

const SectionCard: React.FC<{ title: string; description?: string; children: React.ReactNode }> = ({
  title,
  description,
  children
}) => (
  <div className="bg-gray-800/70 border border-gray-700 rounded-xl p-4 shadow-lg">
    <div className="mb-4">
      <h3 className="text-lg font-semibold text-white">{title}</h3>
      {description && <p className="text-sm text-gray-400 mt-0.5">{description}</p>}
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-sm font-semibold text-gray-200 mb-1.5">{children}</label>
);

const HelperText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs text-gray-400 mt-1">{children}</p>
);

const ToggleRow: React.FC<{ id: string; checked: boolean; onChange: (checked: boolean) => void; label: React.ReactNode }> = ({
  id,
  checked,
  onChange,
  label
}) => (
  <label htmlFor={id} className="flex items-start gap-3 rounded-lg border border-gray-700 bg-gray-900/40 px-3 py-2.5 cursor-pointer hover:border-gray-600 transition-colors">
    <input
      id={id}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 h-4 w-4 rounded border-gray-600 bg-gray-800 text-emerald-500 focus:ring-emerald-500"
    />
    <span className="text-sm text-gray-200">{label}</span>
  </label>
);

const isTabId = (id: string): id is TabId => TAB_CONFIG.some(tab => tab.id === id);

// Game modes that don't have their own settings tab (scenarios, stats, edge calc,
// risk sim, etc.) open the combined "All Settings" tab. Leveling, achievements, and
// branding now live in the Admin Panel, not here.
const mapGameModeToTab = (mode: GameMode): TabId => (isTabId(mode) ? mode : 'all');

type SettingsPanelProps = {
  fullWidth?: boolean;
  onOpen?: () => void;
};

export const SettingsPanel: React.FC<SettingsPanelProps> = ({ fullWidth = false, onOpen }) => {
  const {
    settings,
    updateSettings,
    tableConfig,
    updateTableConfig,
    gameMode
  } = useGameStore();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>(mapGameModeToTab(gameMode));
  // Hide deep realism/tuning knobs by default so the panel stays approachable for a
  // regular user; power users flip this to reveal penetration, surrender variants,
  // bet spread, AI pacing, etc.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const tabsRef = useRef<HTMLDivElement | null>(null);

  // Handle modal open/close effects
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          setIsOpen(false);
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
      };
    } else {
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  // Sync active tab with game mode when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(mapGameModeToTab(gameMode));
    }
  }, [isOpen, gameMode]);

  useEffect(() => {
    if (!tabsRef.current) return;
    requestAnimationFrame(() => {
      const container = tabsRef.current;
      if (!container) return;
      const btn = container.querySelector<HTMLButtonElement>(`[data-tab="${activeTab}"]`);
      if (!btn) return;
      const offsetLeft = btn.offsetLeft - container.offsetLeft;
      const target = offsetLeft - (container.clientWidth - btn.clientWidth) / 2;
      container.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    });
  }, [activeTab, isOpen]);

  const sectionsForActiveTab = useMemo<SectionId[]>(() => {
    if (activeTab === 'all') {
      const currentSections = isTabId(gameMode) ? TAB_SECTIONS[gameMode] : TAB_SECTIONS.training;
      const unique = [...currentSections, ...ALL_SECTIONS].filter((section, idx, arr) => arr.indexOf(section) === idx);
      return unique;
    }
    if (TAB_SECTIONS[activeTab]) return TAB_SECTIONS[activeTab];
    return TAB_SECTIONS.training;
  }, [activeTab, gameMode]);

  const handleChange = (key: string, value: unknown) => {
    updateSettings({ [key]: value });
  };

  const pageDefaults: Record<string, Partial<typeof settings>> = {
    training: {
      numDecks: 6,
      penetration: 75,
      shuffleAfterHand: false,
      dealerHitsSoft17: true,
      doubleAfterSplit: true,
      surrenderAllowed: false,
      earlySurrenderAllowed: false,
      resplitAces: false,
      blackjackPays: 1.5,
      insuranceAllowed: true,
      countingSystem: 'hi-lo',
      startingBankroll: 10000,
      minBet: 25,
      maxBet: 500,
      useDeviations: false,
      hideHandTotals: false,
      soundEnabled: true,
      highContrast: false,
      feedbackMode: 'bad-only',
      casinoName: 'JK Casino'
    },
    'table-simulator': {
      numDecks: 6,
      penetration: 75,
      shuffleAfterHand: false,
      dealerHitsSoft17: true,
      doubleAfterSplit: true,
      surrenderAllowed: false,
      earlySurrenderAllowed: false,
      resplitAces: false,
      blackjackPays: 1.5,
      insuranceAllowed: true,
      countingSystem: 'hi-lo',
      startingBankroll: 10000,
      minBet: 25,
      maxBet: 500,
      useDeviations: false,
      hideHandTotals: false,
      soundEnabled: true,
      highContrast: false,
      feedbackMode: 'bad-only',
      casinoName: 'JK Casino'
    },
    'speed-drill': {
      defaultSpeedDrillDuration: 60,
      defaultSpeedDrillSpeed: 1000
    }
  };

  const restoreDefaults = () => {
    const target = pageDefaults[activeTab] || (activeTab === 'all' ? {
      ...pageDefaults.training,
      ...pageDefaults['speed-drill']
    } : {});
    if (Object.keys(target).length === 0) return;
    updateSettings(target);
  };

  const renderSection = (section: SectionId) => {
    switch (section) {
      case 'shoe':
        return (
          <SectionCard
            title="Shoe & Penetration"
            description="Deck count, penetration, and shuffle cadence for training and full table."
          >
            <div>
              <FieldLabel>Number of Decks: {settings.numDecks}</FieldLabel>
              <input
                type="range"
                min="1"
                max="8"
                value={settings.numDecks}
                onChange={(e) => handleChange('numDecks', parseInt(e.target.value))}
                className="w-full accent-emerald-500"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>1</span>
                <span>2</span>
                <span>4</span>
                <span>6</span>
                <span>8</span>
              </div>
            </div>

            {showAdvanced && (
              <>
                <div>
                  <FieldLabel>Penetration: {settings.penetration}%</FieldLabel>
                  <input
                    type="range"
                    min="50"
                    max="90"
                    value={settings.penetration}
                    onChange={(e) => handleChange('penetration', parseInt(e.target.value))}
                    className="w-full accent-emerald-500"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>50%</span>
                    <span>70%</span>
                    <span>90%</span>
                  </div>
                  <HelperText>Lower penetration makes counting harder; higher penetration yields more edge.</HelperText>
                </div>

                <ToggleRow
                  id="shuffleAfterHand"
                  checked={settings.shuffleAfterHand}
                  onChange={(checked) => handleChange('shuffleAfterHand', checked)}
                  label="Shuffle after every hand (good for pure strategy reps)"
                />
              </>
            )}
          </SectionCard>
        );

      case 'tableRules':
        return (
          <SectionCard
            title="Table Rules"
            description="Rules that affect basic strategy and house edge."
          >
            <div className="grid md:grid-cols-2 gap-3">
              <ToggleRow
                id="dealerHitsSoft17"
                checked={settings.dealerHitsSoft17}
                onChange={(checked) => handleChange('dealerHitsSoft17', checked)}
                label="Dealer hits soft 17"
              />
              <ToggleRow
                id="insuranceAllowed"
                checked={settings.insuranceAllowed}
                onChange={(checked) => handleChange('insuranceAllowed', checked)}
                label="Offer insurance when dealer shows an Ace"
              />
              {showAdvanced && (
                <>
                  <ToggleRow
                    id="doubleAfterSplit"
                    checked={settings.doubleAfterSplit}
                    onChange={(checked) => handleChange('doubleAfterSplit', checked)}
                    label="Double after split allowed"
                  />
                  <ToggleRow
                    id="surrenderAllowed"
                    checked={settings.surrenderAllowed}
                    onChange={(checked) => handleChange('surrenderAllowed', checked)}
                    label="Late surrender allowed"
                  />
                  <ToggleRow
                    id="earlySurrenderAllowed"
                    checked={settings.earlySurrenderAllowed}
                    onChange={(checked) => handleChange('earlySurrenderAllowed', checked)}
                    label="Early surrender allowed"
                  />
                  <ToggleRow
                    id="resplitAces"
                    checked={settings.resplitAces}
                    onChange={(checked) => handleChange('resplitAces', checked)}
                    label="Resplit aces allowed"
                  />
                </>
              )}
            </div>

            <div>
              <FieldLabel>Blackjack Payout</FieldLabel>
              <select
                value={settings.blackjackPays}
                onChange={(e) => handleChange('blackjackPays', parseFloat(e.target.value))}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500"
              >
                <option value="1.5">3:2 (standard)</option>
                <option value="1.2">6:5 (penalty)</option>
                <option value="1">1:1 (worst)</option>
              </select>
              <HelperText>Use realistic rules for the casino you practice for.</HelperText>
            </div>
          </SectionCard>
        );

      case 'counting':
        return (
          <SectionCard
            title="Counting System"
            description="Pick the system to train against and preview tag values."
          >
            <select
              value={settings.countingSystem}
              onChange={(e) => handleChange('countingSystem', e.target.value)}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500"
            >
              {Object.entries(COUNTING_SYSTEMS).map(([key, system]) => (
                <option key={key} value={key}>
                  {system.name}
                </option>
              ))}
            </select>
            <HelperText>{COUNTING_SYSTEMS[settings.countingSystem].description}</HelperText>

            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 text-xs">
              {Object.entries(COUNTING_SYSTEMS[settings.countingSystem].values).map(([rank, value]) => (
                <div key={rank} className="flex justify-between bg-gray-900 border border-gray-700 px-2 py-1.5 rounded">
                  <span className="text-gray-200 font-semibold">{rank}</span>
                  <span className={value > 0 ? 'text-green-400' : value < 0 ? 'text-red-400' : 'text-gray-400'}>
                    {value > 0 ? '+' : ''}{value}
                  </span>
                </div>
              ))}
            </div>
          </SectionCard>
        );

      case 'speedDefaults':
        return (
          <SectionCard
            title="Speed Drill Defaults"
            description="Default duration and speed when starting a drill."
          >
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <FieldLabel>Default Duration</FieldLabel>
                <select
                  value={DURATION_PRESETS.includes(settings.defaultSpeedDrillDuration) ? settings.defaultSpeedDrillDuration : 'custom'}
                  onChange={(e) => {
                    if (e.target.value === 'custom') return;
                    handleChange('defaultSpeedDrillDuration', parseInt(e.target.value));
                  }}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500"
                >
                  {DURATION_PRESETS.map(d => (
                    <option key={d} value={d}>{d}s</option>
                  ))}
                  {showAdvanced && <option value="custom">Custom</option>}
                </select>
                {showAdvanced && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={10}
                      max={600}
                      value={DURATION_PRESETS.includes(settings.defaultSpeedDrillDuration) ? '' : settings.defaultSpeedDrillDuration}
                      onChange={(e) => handleChange('defaultSpeedDrillDuration', Math.max(10, Math.min(600, parseInt(e.target.value) || 10)))}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500"
                      placeholder=""
                    />
                    <span className="text-sm text-gray-400">s</span>
                  </div>
                )}
                <HelperText>Used when starting a new drill.</HelperText>
              </div>
              <div className="space-y-2">
                <FieldLabel>Default Speed</FieldLabel>
                <select
                  value={SPEED_PRESETS.some(s => s.value === settings.defaultSpeedDrillSpeed) ? settings.defaultSpeedDrillSpeed : 'custom'}
                  onChange={(e) => {
                    if (e.target.value === 'custom') return;
                    handleChange('defaultSpeedDrillSpeed', parseInt(e.target.value));
                  }}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500"
                >
                  {SPEED_PRESETS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                  {showAdvanced && <option value="custom">Custom</option>}
                </select>
                {showAdvanced && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0.25}
                      max={5}
                      step={0.05}
                      value={SPEED_PRESETS.some(s => s.value === settings.defaultSpeedDrillSpeed) ? '' : Number((1000 / settings.defaultSpeedDrillSpeed).toFixed(2))}
                      onChange={(e) => {
                        const mult = parseFloat(e.target.value);
                        if (Number.isNaN(mult)) return;
                        const ms = Math.max(200, Math.min(2000, Math.round(1000 / mult)));
                        handleChange('defaultSpeedDrillSpeed', ms);
                      }}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500"
                      placeholder=""
                    />
                    <span className="text-sm text-gray-400">x</span>
                  </div>
                )}
                <HelperText>Lower is faster. 1x ≈ 1000ms between cards.</HelperText>
              </div>
            </div>
          </SectionCard>
        );

      case 'bankroll':
        return (
          <SectionCard
            title="Bankroll & Limits"
            description="Tune bankroll assumptions and betting spread."
          >
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <FieldLabel>Starting Bankroll</FieldLabel>
                <input
                  type="number"
                  min={100}
                  max={500000}
                  step={500}
                  value={settings.startingBankroll}
                  onChange={(e) => handleChange('startingBankroll', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500"
                />
                <HelperText>Used for session UI and bet guidance.</HelperText>
              </div>
              {showAdvanced && (
                <>
                  <div>
                    <FieldLabel>Minimum Bet</FieldLabel>
                    <input
                      type="number"
                      min={1}
                      max={1000}
                      step={1}
                      value={settings.minBet}
                      onChange={(e) => handleChange('minBet', Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500"
                    />
                    <HelperText>Also gates dealing when using keyboard shortcuts.</HelperText>
                  </div>
                  <div>
                    <FieldLabel>Maximum Bet</FieldLabel>
                    <input
                      type="number"
                      min={50}
                      max={20000}
                      step={50}
                      value={settings.maxBet}
                      onChange={(e) => handleChange('maxBet', Math.max(10, parseInt(e.target.value) || 0))}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500"
                    />
                    <HelperText>Used by betting chips and feedback ranges.</HelperText>
                  </div>
                </>
              )}
            </div>
          </SectionCard>
        );

      case 'trainingAids':
        return (
          <SectionCard
            title="Training Aids & Feedback"
            description="Toggle coaching helpers that apply to training, scenarios, and table sim."
          >
            <div className="grid md:grid-cols-2 gap-3">
              <ToggleRow
                id="soundEnabled"
                checked={settings.soundEnabled}
                onChange={(checked) => handleChange('soundEnabled', checked)}
                label="Play sound effects for actions and results"
              />
              <ToggleRow
                id="highContrast"
                checked={settings.highContrast}
                onChange={(checked) => handleChange('highContrast', checked)}
                label="High contrast visuals (accessibility)"
              />
              {showAdvanced && (
                <>
                  <ToggleRow
                    id="useDeviations"
                    checked={settings.useDeviations}
                    onChange={(checked) => handleChange('useDeviations', checked)}
                    label="Use Illustrious 18 deviations when true count calls for it"
                  />
                  <ToggleRow
                    id="hideHandTotals"
                    checked={settings.hideHandTotals}
                    onChange={(checked) => handleChange('hideHandTotals', checked)}
                    label="Hide hand totals to force mental counting"
                  />
                </>
              )}
            </div>

            <div>
              <FieldLabel>Feedback Mode</FieldLabel>
              <select
                value={settings.feedbackMode}
                onChange={(e) => handleChange('feedbackMode', e.target.value)}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500"
              >
                <option value="bad-only">Only show mistakes</option>
                <option value="both">Show all feedback</option>
              </select>
              <HelperText>Controls decision, bet, and insurance banners.</HelperText>
            </div>
          </SectionCard>
        );

      case 'tableSim':
        return (
          <SectionCard
            title="Full Table Layout"
            description="Seat count, pacing, and AI behavior for the table simulator."
          >
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <FieldLabel>Number of Seats: {tableConfig.numberOfSeats}</FieldLabel>
                <input
                  type="range"
                  min="5"
                  max="7"
                  value={tableConfig.numberOfSeats}
                  onChange={(e) => updateTableConfig({ numberOfSeats: parseInt(e.target.value) })}
                  className="w-full accent-emerald-500"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>5</span>
                  <span>6</span>
                  <span>7</span>
                </div>
              </div>
              <div>
                <FieldLabel>AI Players: {tableConfig.activePlayers}</FieldLabel>
                <input
                  type="range"
                  min="0"
                  max={Math.max(0, tableConfig.numberOfSeats - tableConfig.userHandsCount)}
                  value={tableConfig.activePlayers}
                  onChange={(e) => updateTableConfig({ activePlayers: parseInt(e.target.value) })}
                  className="w-full accent-emerald-500"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>0</span>
                  <span>{Math.floor(tableConfig.numberOfSeats / 2)}</span>
                  <span>{Math.max(0, tableConfig.numberOfSeats - tableConfig.userHandsCount)}</span>
                </div>
              </div>
              <div>
                <FieldLabel>Your Hands: {tableConfig.userHandsCount}</FieldLabel>
                <input
                  type="range"
                  min="1"
                  max="3"
                  value={tableConfig.userHandsCount}
                  onChange={(e) => {
                    const count = parseInt(e.target.value);
                    const center = Math.floor(tableConfig.numberOfSeats / 2);
                    const positions = Array.from({ length: count }, (_, i) => center - Math.floor(count / 2) + i);
                    updateTableConfig({
                      userHandsCount: count,
                      userSeatPositions: positions
                    });
                  }}
                  className="w-full accent-emerald-500"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>1</span>
                  <span>2</span>
                  <span>3</span>
                </div>
              </div>
              <div className="rounded-lg border border-gray-700 bg-gray-900/40 px-3 py-2 text-sm text-gray-200">
                <div className="font-semibold mb-1">Seat preview</div>
                <div className="text-xs text-gray-400">You occupy: {tableConfig.userSeatPositions.map((s) => s + 1).join(', ')}</div>
                <div className="text-xs text-gray-400 mt-1">Adjust before jumping into Full Table mode.</div>
              </div>
              {showAdvanced && (
              <div className="md:col-span-2 grid md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel>AI Think Time Range</FieldLabel>
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                    <span>Min: {(settings.aiThinkTimeMinMs / 1000).toFixed(1)}s</span>
                    <span>Max: {(settings.aiThinkTimeMaxMs / 1000).toFixed(1)}s</span>
                  </div>
                  <div className="flex gap-3 items-center">
                    <input
                      type="range"
                      min={500}
                      max={8000}
                      step={100}
                      value={settings.aiThinkTimeMinMs}
                      onChange={(e) => {
                        const min = Math.min(parseInt(e.target.value), settings.aiThinkTimeMaxMs - 200);
                        handleChange('aiThinkTimeMinMs', min);
                      }}
                      className="w-full accent-emerald-500"
                    />
                    <input
                      type="range"
                      min={700}
                      max={8000}
                      step={100}
                      value={settings.aiThinkTimeMaxMs}
                      onChange={(e) => {
                        const max = Math.max(parseInt(e.target.value), settings.aiThinkTimeMinMs + 200);
                        handleChange('aiThinkTimeMaxMs', max);
                      }}
                      className="w-full accent-emerald-500"
                    />
                  </div>
                  <HelperText>Default 3–5s. Harder spots bias toward the upper end.</HelperText>
                </div>
                <div>
                  <FieldLabel>AI Mistake Rate: {(settings.aiMistakeRate * 100).toFixed(0)}%</FieldLabel>
                  <input
                    type="range"
                    min={0}
                    max={30}
                    step={1}
                    value={Math.round(settings.aiMistakeRate * 100)}
                    onChange={(e) => handleChange('aiMistakeRate', Math.max(0, Math.min(1, parseInt(e.target.value) / 100)))}
                    className="w-full accent-emerald-500"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>0%</span>
                    <span>15%</span>
                    <span>30%</span>
                  </div>
                  <HelperText>When triggered, AI flips common errors (hit ↔ stand) for realism.</HelperText>
                </div>
              </div>
              )}
            </div>
          </SectionCard>
        );

      default:
        return null;
    }
  };

  const handleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen && onOpen) onOpen();
  };

  return (
    <div className={fullWidth ? 'w-full' : 'relative'}>
      <button
        onClick={handleOpen}
        title="Settings"
        className={`relative px-4 py-1.5 rounded-full text-sm font-bold whitespace-nowrap flex items-center gap-1 overflow-hidden border-2 border-transparent transition-all duration-200 group ${
          fullWidth ? 'w-full py-2.5' : ''
        } ${
          isOpen 
            ? 'bg-gradient-to-br from-emerald-600 to-emerald-800 text-white' 
            : 'text-gray-400 hover:text-white'
        }`}
      >
        {/* Chip hover effect */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
        <div className="absolute inset-0 rounded-full border-2 border-emerald-400/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
        <div className="absolute inset-0.5 rounded-full border border-dashed border-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
        <svg className="w-4 h-4 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className={`relative z-10 ${fullWidth ? '' : 'hidden min-[1060px]:inline group-hover:inline'}`}>Settings</span>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 animate-page-in"
            onClick={() => setIsOpen(false)}
          />

          <div className="fixed inset-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 bg-gray-900 rounded-none sm:rounded-2xl shadow-2xl p-4 sm:p-7 z-50 w-full max-w-full sm:max-w-3xl h-full sm:h-auto max-h-full sm:max-h-[90vh] border border-gray-800 flex flex-col overflow-hidden">
            <div className="h-full flex flex-col overflow-hidden animate-page-in">
            <div className="flex flex-col gap-4 mb-4 flex-none">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    Control Center
                  </h2>
                  <p className="text-sm text-gray-400">
                    Tabs reflect each page; the current page opens first for quicker tweaks.
                  </p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-white text-2xl leading-none"
                  aria-label="Close settings"
                >
                  ×
                </button>
              </div>

              <div ref={tabsRef} className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" role="tablist" aria-label="Settings sections">
                {TAB_CONFIG.map(({ id, label, description, icon }) => {
                  const isActive = activeTab === id;
                  return (
                    <button
                      key={id}
                      data-tab={id}
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveTab(id)}
                      className={`flex-shrink-0 px-3 py-2 rounded-xl border transition-all text-left min-w-[130px] ${
                        isActive
                          ? 'border-emerald-500/70 bg-emerald-500/10 text-white shadow-inner'
                          : 'border-gray-700 bg-gray-800/70 text-gray-300 hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span>{icon}</span>
                        <span className="font-semibold text-sm">{label}</span>
                      </div>
                      <div className="text-[11px] text-gray-400 mt-0.5">{description}</div>
                    </button>
                  );
                })}
              </div>

              <label
                htmlFor="showAdvancedSettings"
                className="flex items-center justify-between gap-3 rounded-lg border border-gray-700 bg-gray-800/70 px-3 py-2 cursor-pointer hover:border-gray-600 transition-colors"
              >
                <span className="text-sm text-gray-200">
                  <span className="font-semibold">Advanced settings</span>
                  <span className="text-gray-400"> — penetration, surrender rules, bet spread, AI pacing</span>
                </span>
                <input
                  id="showAdvancedSettings"
                  type="checkbox"
                  checked={showAdvanced}
                  onChange={(e) => setShowAdvanced(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-emerald-500 focus:ring-emerald-500"
                />
              </label>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 pr-1">
              <div className="space-y-4 pb-6">
                {sectionsForActiveTab.map((section) => (
                  <React.Fragment key={section}>{renderSection(section)}</React.Fragment>
                ))}

                {sectionsForActiveTab.length === 0 && (
                  <div className="rounded-lg border border-gray-700 bg-gray-800/70 p-4 text-sm text-gray-300">
                    No specific settings for this page yet. Use the Shared tab for global tweaks.
                  </div>
                )}
              </div>
            </div>

            <div className="flex-none bg-gray-900 border-t border-gray-800 pt-3 pb-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    if (confirm('Restore defaults for this page?')) {
                      restoreDefaults();
                    }
                  }}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
                >
                  Restore Defaults
                </button>

                <button
                  onClick={() => setIsOpen(false)}
                  className="flex-1 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-colors"
                >
                  Save & Close
                </button>
              </div>
            </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
